import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog } from 'radix-ui';
import { X, ZoomIn, ZoomOut, Check } from 'lucide-react';

interface Props {
  file: File | null;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
}

const VIEWPORT = 320;
const OUTPUT_SIZE = 800;

export function ImageCropper({ file, onCancel, onConfirm }: Props) {
  const [src, setSrc] = useState('');
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });

  useEffect(() => {
    if (!file) {
      setImg(null);
      setSrc('');
      return;
    }
    const url = URL.createObjectURL(file);
    setSrc(url);
    const i = new Image();
    i.onload = () => {
      setImg(i);
      setScale(1);
      setOffset({ x: 0, y: 0 });
    };
    i.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const baseSize = img ? Math.max(VIEWPORT / img.width, VIEWPORT / img.height) : 1;
  const w = img ? img.width * baseSize * scale : 0;
  const h = img ? img.height * baseSize * scale : 0;

  const clamp = useCallback((ox: number, oy: number) => {
    if (!img) return { x: 0, y: 0 };
    const maxX = Math.max(0, (w - VIEWPORT) / 2);
    const maxY = Math.max(0, (h - VIEWPORT) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, ox)),
      y: Math.max(-maxY, Math.min(maxY, oy)),
    };
  }, [img, w, h]);

  useEffect(() => {
    setOffset(o => clamp(o.x, o.y));
  }, [scale, clamp]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!img) return;
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    const nx = dragStart.current.ox + (e.clientX - dragStart.current.x);
    const ny = dragStart.current.oy + (e.clientY - dragStart.current.y);
    setOffset(clamp(nx, ny));
  };
  const onPointerUp = (e: React.PointerEvent) => {
    setDragging(false);
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
  };

  const handleConfirm = () => {
    if (!img) return;
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const tx = (VIEWPORT - w) / 2 + offset.x;
    const ty = (VIEWPORT - h) / 2 + offset.y;
    const factor = baseSize * scale;
    const sx = -tx / factor;
    const sy = -ty / factor;
    const sSize = VIEWPORT / factor;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

    const mime = file?.type === 'image/png' ? 'image/png' : 'image/jpeg';
    canvas.toBlob(blob => {
      if (blob) onConfirm(blob);
    }, mime, 0.92);
  };

  return (
    <Dialog.Root open={!!file} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-[min(420px,calc(100vw-2rem))] bg-white rounded-[2rem] shadow-2xl p-6 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
          <Dialog.Title className="text-2xl font-extrabold text-slate-900 tracking-tight mb-1">
            Crop image
          </Dialog.Title>
          <Dialog.Description className="text-slate-500 text-sm font-medium mb-5">
            Drag to reposition, slide to zoom. The square area will be saved.
          </Dialog.Description>

          <div
            className="relative mx-auto bg-slate-900 overflow-hidden rounded-2xl touch-none select-none"
            style={{ width: VIEWPORT, height: VIEWPORT, cursor: img ? (dragging ? 'grabbing' : 'grab') : 'default' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {img && (
              <img
                src={src}
                alt=""
                draggable={false}
                style={{
                  position: 'absolute',
                  left: (VIEWPORT - w) / 2 + offset.x,
                  top: (VIEWPORT - h) / 2 + offset.y,
                  width: w,
                  height: h,
                  maxWidth: 'none',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              />
            )}
            <div className="pointer-events-none absolute inset-0 ring-2 ring-white/90 rounded-2xl shadow-[0_0_0_9999px_rgba(15,23,42,0.35)]" />
          </div>

          <div className="flex items-center gap-3 mt-5 px-1">
            <ZoomOut className="w-5 h-5 text-slate-400 flex-shrink-0" />
            <input
              type="range"
              min={1}
              max={4}
              step={0.01}
              value={scale}
              onChange={e => setScale(parseFloat(e.target.value))}
              className="flex-1 accent-[#2563eb]"
              aria-label="Zoom"
            />
            <ZoomIn className="w-5 h-5 text-slate-400 flex-shrink-0" />
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 h-12 px-4 bg-slate-100 text-slate-600 rounded-2xl font-bold hover:bg-slate-200 transition-all active:scale-[0.98]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!img}
              className="flex-1 h-12 px-4 bg-[#2563eb] text-white rounded-2xl font-bold hover:bg-[#1d4ed8] transition-all active:scale-[0.98] shadow-lg shadow-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Check className="w-5 h-5" />
              Crop &amp; Upload
            </button>
          </div>

          <Dialog.Close asChild>
            <button
              type="button"
              aria-label="Close"
              className="absolute top-5 right-5 w-9 h-9 rounded-full flex items-center justify-center hover:bg-slate-100 active:scale-95 transition-all"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
